/**
 * The document header and the delivery address.
 *
 * Editable because the parser can misread, not so the numbers can be
 * renegotiated: everything typed here is re-checked server-side on import.
 */
import type { InkooporderDocument, ReviewContext } from '@/api/easykitInkooporder';
import { inputClass } from './InkooporderFormat';

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-td-muted font-semibold">{label}</dt>
      <dd className="text-sm text-td-navy">{value || '—'}</dd>
    </div>
  );
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (next: string) => void }) {
  const id = `veld-${label.toLowerCase().replace(/[^a-z]/g, '')}`;
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] uppercase tracking-wide text-td-muted font-semibold">
        {label}
      </label>
      <input id={id} className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

interface Props {
  doc: InkooporderDocument;
  context: ReviewContext | null;
  totalM2: number;
  onPatch: (change: Partial<InkooporderDocument>) => void;
}

export function InkooporderFields({ doc, context, totalM2, onPatch }: Props) {
  const patchDelivery = (change: Partial<InkooporderDocument['delivery']>) =>
    onPatch({ delivery: { ...doc.delivery, ...change } });

  return (
    <>
      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-bold text-td-navy mb-3">Document</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <TextField label="Referentie" value={doc.orderNumber} onChange={(v) => onPatch({ orderNumber: v })} />
          <TextField label="Besteldatum" value={doc.orderDate ?? ''} onChange={(v) => onPatch({ orderDate: v || null })} />
          <TextField label="Leverdatum" value={doc.deliveryDate ?? ''} onChange={(v) => onPatch({ deliveryDate: v || null })} />
          <TextField label="Filiaalcode" value={doc.departmentCode ?? ''} onChange={(v) => onPatch({ departmentCode: v || null })} />
          <ReadOnlyField label="Betaling" value={doc.paymentTerms ?? ''} />
          <ReadOnlyField label="Verzending" value={doc.shippingMethod ?? ''} />
        </div>
        <dl className="text-xs text-td-muted mt-3 space-y-1">
          {context?.branchName && (
            <div>Filiaal in Odoo: <span className="text-td-navy font-semibold">{context.branchName}</span></div>
          )}
          <div>
            Werf in Odoo: <span className="text-td-navy font-semibold">{doc.delivery.name || '—'}</span>
            <span className="mx-2 opacity-40">·</span>
            Totaal offerte: <span className="text-td-navy font-semibold">{totalM2} m²</span>
          </div>
        </dl>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-bold text-td-navy mb-3">Leveradres</h2>
        {context?.existingShippingName ? (
          <p className="text-sm text-td-navy">
            Dit is een adres van Easykit zelf. Er wordt geen nieuw contact gemaakt; de order gaat
            naar <span className="font-semibold">{context.existingShippingName}</span>.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <TextField label="Naam" value={doc.delivery.name} onChange={(v) => patchDelivery({ name: v })} />
            <TextField label="Straat" value={doc.delivery.street} onChange={(v) => patchDelivery({ street: v })} />
            <TextField label="Postcode" value={doc.delivery.postcode} onChange={(v) => patchDelivery({ postcode: v })} />
            <TextField label="Gemeente" value={doc.delivery.city} onChange={(v) => patchDelivery({ city: v })} />
            <TextField label="Telefoon" value={doc.delivery.phone} onChange={(v) => patchDelivery({ phone: v })} />
            <TextField label="E-mail" value={doc.delivery.email} onChange={(v) => patchDelivery({ email: v })} />
          </div>
        )}
      </section>
    </>
  );
}
